import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from 'components/Tooltip';
import { fetchSidewalkData, clearSidewalkData } from 'data/reducers/sidewalkReducer';

import './SidewalkMap.css';

const SidewalkMap = ({ propertyData }) => {
	const dispatch = useDispatch();
	const mapContainerRef = useRef(null);
	const mapRef = useRef(null);
	const currentLayersRef = useRef([]);
	const propertyId = propertyData?.id;

	// Get data from Redux store
	const { 
		sidewalkData, 
		propertyData: fetchedProperty, 
		// isLoading, 
		error 
	} = useSelector((state) => state.sidewalk);

	// Function to get line weight based on zoom level
	const getLineWeight = useCallback((zoom) => {
		if (zoom >= 18) return 8;
		if (zoom >= 16) return 7;
		if (zoom >= 14) return 6;
		return 5;
	}, []);

	// Color function
	const getColorForWidth = useCallback((width) => {
		if (!width) return '#999999';
		if (width < 6) return '#8B0000';
		if (width < 8) return '#A0522D';
		if (width < 10) return '#DAA520';
		if (width < 12) return '#B8860B';
		if (width < 15) return '#556B2F';
		if (width < 18) return '#228B22';
		return '#00008B';
	}, []);

	// Function to update layer styles based on zoom
	const updateLayerStyles = useCallback(() => {
		if (!mapRef.current) return;
		const zoom = mapRef.current.getZoom();
		const weight = getLineWeight(zoom);

		currentLayersRef.current.forEach(layer => {
			if (layer.setStyle && layer.options && layer.options.originalColor) {
				// Update line segments with dynamic weight
				layer.setStyle({
					weight: weight,
					color: layer.options.originalColor,
					opacity: 0.9,
					lineCap: 'round',
					lineJoin: 'round'
				});
			}
		});
	}, [getLineWeight]);

	// Clear all layers from map
	const clearMapLayers = useCallback(() => {
		currentLayersRef.current.forEach(layer => {
			if (mapRef.current && mapRef.current.hasLayer(layer)) {
				mapRef.current.removeLayer(layer);
			}
		});
		currentLayersRef.current = [];
	}, []);

	// Render map with data from Redux
	const renderMapData = useCallback(() => {
		if (!fetchedProperty || !sidewalkData || !mapContainerRef.current) return;

		// Clear existing layers if reloading
		if (mapRef.current) {
			clearMapLayers();
		}

		// Initialize map if not already initialized
		if (!mapRef.current && mapContainerRef.current) {
			mapRef.current = window.L.map(mapContainerRef.current).setView([fetchedProperty.lat, fetchedProperty.long], 16);

			// Add tile layer
			window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				attribution: '© OpenStreetMap contributors',
				maxZoom: 22,
			}).addTo(mapRef.current);

			// Add zoom event listener to update line weights
			mapRef.current.on('zoomend', updateLayerStyles);
		} else if (mapRef.current) {
			// Just recenter the map
			mapRef.current.setView([fetchedProperty.lat, fetchedProperty.long], 16);
		}

		// Add property marker
		const marker = window.L.marker([fetchedProperty.lat, fetchedProperty.long]).addTo(mapRef.current);
		marker.bindPopup(`
			<div style="font-family: sans-serif;">
				${fetchedProperty.address || 'N/A'}
			</div>
		`).openPopup();
		currentLayersRef.current.push(marker);

		// Add sidewalk polygons and width segments
		sidewalkData.sidewalks.forEach((sidewalk, index) => {
			try {
				// 1. Add the full polygon as a light background (optional)
				const geometry = typeof sidewalk.the_geom === 'string'
					? JSON.parse(sidewalk.the_geom)
					: sidewalk.the_geom;

				if (geometry?.coordinates) {
					const geoJsonLayer = window.L.geoJSON(geometry, {
						style: {
							fillColor: '#cccccc',
							weight: 1,
							opacity: 0.3,
							color: '#999',
							fillOpacity: 0.2
						}
					}).addTo(mapRef.current);

					geoJsonLayer.bindPopup(`
						<div style="font-family: sans-serif; min-width: 200px;">
							<strong>Sidewalk #${index + 1}</strong><br>
							<strong>Source ID:</strong> ${sidewalk.source_id}<br>
							<strong>Status:</strong> ${sidewalk.status}<br>
							<em>Click on colored lines to see width at that point</em>
						</div>
					`);

					currentLayersRef.current.push(geoJsonLayer);
				}

				// 2. Add width segments as colored lines along centerline
				if (sidewalk.width_segments && sidewalk.width_segments.length > 0) {
					sidewalk.width_segments.forEach((segment, segmentIndex) => {
						const width = segment.properties.width;
						const color = getColorForWidth(width);

						const segmentLayer = window.L.geoJSON(segment, {
							style: {
								color: color,
								weight: getLineWeight(16), // Initial weight for zoom level 16
								opacity: 0.9,
								lineCap: 'round',
								lineJoin: 'round'
							},
							originalColor: color // Store original color for zoom updates
						}).addTo(mapRef.current);

						segmentLayer.bindPopup(`
							<div style="font-family: sans-serif; min-width: 200px;">
								<strong style="color: ${color}; font-size: 16px;">Width: ${width} ft</strong><br>
								<strong>Width (m):</strong> ${segment.properties.width_meters} m<br>
								<strong>Start width:</strong> ${segment.properties.width_start} ft<br>
								<strong>End width:</strong> ${segment.properties.width_end} ft<br>
								<strong>Segment:</strong> ${segmentIndex + 1} of ${sidewalk.width_segments.length}<br>
								<strong>Sidewalk ID:</strong> ${sidewalk.source_id}
							</div>
						`);

						currentLayersRef.current.push(segmentLayer);
					});
				}
			} catch (err) {
				console.error(`Error rendering sidewalk ${sidewalk.source_id}:`, err);
			}
		});
	}, [fetchedProperty, sidewalkData, clearMapLayers, getColorForWidth, getLineWeight, updateLayerStyles]);

	// Load data when property ID changes
	useEffect(() => {
		if (propertyId) {
			dispatch(fetchSidewalkData(propertyId));
		}
		return () => {
			dispatch(clearSidewalkData());
		};
	}, [propertyId, dispatch]);

	// Initialize Leaflet library
	useEffect(() => {
		// Load Leaflet if not already loaded
		if (typeof window.L === 'undefined') {
			const leafletCSS = document.createElement('link');
			leafletCSS.rel = 'stylesheet';
			leafletCSS.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
			document.head.appendChild(leafletCSS);

			const leafletJS = document.createElement('script');
			leafletJS.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
			document.head.appendChild(leafletJS);
		}

		return () => {
			// Cleanup map when component unmounts
			if (mapRef.current) {
				mapRef.current.off('zoomend', updateLayerStyles);
				mapRef.current.remove();
				mapRef.current = null;
			}
		};
	}, [updateLayerStyles]);

	// Render map when data is available
	useEffect(() => {
		if (fetchedProperty && sidewalkData && typeof window.L !== 'undefined') {
			renderMapData();
		}
	}, [fetchedProperty, sidewalkData, renderMapData]);

	return (
		<div className="leaflet-map-wrapper">
			<div className="sidewalk-map-container">
				<div className="sidewalk-map-content">
					{error ? (
						<div className="map-error-message">
							{error}
						</div>
					) : (
						<div className="sidewalk-map" ref={mapContainerRef}></div>
					)}

					{!error && <div className="sidewalk-info-icon">
					<Tooltip
						text={`
							<div class="sidewalk-tooltip-content">
								<div class="tooltip-title">Sidewalk Widths</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #8B0000;"></div>
									<span>&lt; 6 ft (Non-ADA)</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #A0522D;"></div>
									<span>6 - 8 ft</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #DAA520;"></div>
									<span>8 - 10 ft</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #B8860B;"></div>
									<span>10 - 12 ft</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #556B2F;"></div>
									<span>12 - 15 ft</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #228B22;"></div>
									<span>15 - 18 ft</span>
								</div>
								<div class="tooltip-item">
									<div class="tooltip-color" style="background: #00008B;"></div>
									<span>18+ ft</span>
								</div>
							</div>
						`}
						isHtml={true}
					>
						<i className="bi bi-info-circle-fill"></i>
					</Tooltip>
				</div>}
				</div>
			</div>
		</div>
	);
};

export default SidewalkMap;